import { shared } from '@tig-dpqa-cloud/contract-backend-common';
import { z } from 'zod';

/**
 * Validates passport identifier fields (caseName, slideName).
 * Rejects if the value contains suspicious HTML/injection characters: < > & " ' %
 * @param value - The string to validate
 * @returns true if valid, false if contains suspicious characters
 */
const validatePassportIdentifier = (value?: string): boolean => {
  if (!value || value.length === 0) {
    return true;
  }

  // Reject if contains any HTML injection characters
  return !/[<>&"'%]/.test(value);
};

const image = z.looseObject({
  path: z.string().min(1).describe('Path of the file'),
  dimensions: z.tuple([
    z.int().nonnegative().describe('Width of the image in pixels'),
    z.int().nonnegative().describe('Height of the image in pixels'),
  ]).optional(),
});

export const integratorType = z.enum(['SECTRA', 'SECTRA-MOCK', 'GENERIC_ADAPTER']).describe('The type of integrator');
export type IntegratorType = z.infer<typeof integratorType>;

export const BaseIntegrator = z.object({
  type: integratorType,
});

export const SectraIntegrator = BaseIntegrator.extend({
  type: z.enum([integratorType.enum.SECTRA, integratorType.enum['SECTRA-MOCK']]),
  callbackUrl: z.string(),
  inputTriggerReceivedAt: z.iso.datetime({ offset: true }).optional().describe('Timestamp when the input trigger was received in the dataplane, in ISO 8601 format'),
  headers: z.object({
    apiVersion: z.string().describe('Sectra version'),
    softwareVersion: z.string().describe('dataplane api version'),
  }),
}).passthrough().describe('Integrator for Sectra');
export type SectraIntegratorType = z.infer<typeof SectraIntegrator>;

export const GenericAdapterIntegrator = BaseIntegrator.extend({
  type: z.literal(integratorType.enum['GENERIC_ADAPTER']),
  url: z.string().optional().describe('The URL where the generic adapter can be reached'),
}).passthrough().describe('Integrator for Generic Adapter');
export type GenericAdapterIntegratorType = z.infer<typeof GenericAdapterIntegrator>;

export const Integrator = z.discriminatedUnion('type', [SectraIntegrator, GenericAdapterIntegrator]);
export type IntegratorTsType = z.infer<typeof Integrator>;

export const WSIOriginBase = z.looseObject({
  externalSlideScanId: z.string().describe('The slide-scan identifier provided by the integrator'),
  version: z.string().optional().describe('Version of the origin within the passport'),
  integrator: z.looseObject(BaseIntegrator.shape).optional(),
});

export type WSIOriginBaseType = z.infer<typeof WSIOriginBase>;

export const WSIOrigin = z.looseObject(WSIOriginBase.extend({
  version: z.string().optional().describe('Version of the origin within the passport'),
  location: z.string().optional().describe('The location of the passport on the dataplane slide-scans-working-dir'),
  integrator: Integrator.describe('Integrator information of the WSI origin'),
}).shape);

export type WSIOriginType = z.infer<typeof WSIOrigin>;

export const coordinate = z.object({
  x: z.number().describe('X coordinate'),
  y: z.number().describe('Y coordinate'),
});

export const area = z.object({
  topLeft: coordinate.describe('Top left corner of the area'),
  bottomRight: coordinate.describe('Right bottom corner of the area'),
});

export const WSIPassport = shared.WSIPassportBase.extend({
  schemaVersion: z.string().optional().describe('Version of the WSI passport schema'), // it should be required, but was not defined in 1.0.0 (Albus release), so keep it optional now
  origin: WSIOrigin.describe('Origin of the WSI'),
  modelsConfigVersion: shared.modelsConfigVersion.optional(),
  slideScanMetadata: z.looseObject({
    maxMagnification: z.number().nonnegative().optional().describe('Magnification of the slide'),
    rackNumber: z.number().nonnegative().optional().describe('Rack number'),
    rackPosition: z.number().nonnegative().optional().describe('Rack position'),
    scanner: z.looseObject({
      serialNumber: z.string().max(1000).optional().describe('Serial number of the scanner'),
      vendor: z.looseObject({
        scannerType: z.string().describe('The type of scanner').optional(),
        name: z.string().describe('The name of the scanner vendor').optional(),
      }).optional(),
    })
      .optional()
      .describe('Scanner information'),
    slide: z.looseObject({
      physicalId: z.string().optional().describe('Physical id of a slide'),
      caseName: z.string().max(255).optional().refine(
        (value) => validatePassportIdentifier(value),
        { message: 'Case name contains invalid characters.' },
      ).describe('Case name'),
      slideName: z.string().max(255).optional().refine(
        (value) => validatePassportIdentifier(value),
        { message: 'Slide name contains invalid characters.' },
      ).describe('Slide name'),
      slideScanDisplayName: z.string().optional().describe('Display name of the slide scan'),
    })
      .optional()
      .describe('Slide information'),
    staining: z.string().optional().describe('Staining of the slide'),
    bodyPart: z.string().optional().describe('Body part of the slide'),
    customField1: z.string().optional().describe('Custom field 1'),
  })
    .describe('Metadata of the slide scan')
    .optional(),
  label: image.optional().describe('Label image of the WSI file'),
  macro: z.looseObject(image.extend({
    originalArea: area.optional().describe('Original area of the macro image'),
    croppedArea: area.optional().describe('Cropped area of the macro image'),
    rotation: z.int().refine((val) => [0, 90, 180, 270].includes(val), {
      message: 'Rotation must be 0, 90, 180, or 270 degrees',
    }).optional().describe('Degrees the macro was rotated (counter-clockwise) from its original portrait orientation to landscape'),
  }).shape).optional().describe('Macro image of the WSI file.'),
  thumbnail: image.optional().describe('Thumbnail image of the WSI file'),
  // Optional for now until DP has implemented this
  triggeredAt: z.iso.datetime({ offset: true })
    .pipe(z.coerce.date())
    .optional()
    .describe('Date, time and zone when the slide was triggered in the dataplane'),
}).passthrough().describe('Only defines the values necessary for the back-end');

export type WSIPassportType = z.infer<typeof WSIPassport>;
export type WSIPassportInputType = z.input<typeof WSIPassport>;
